import React, { useState, useEffect, useRef } from 'react';
import SearchableSelect from '../../components/SearchableSelect';

const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Base category list — same set Resolution Master already offers, so an
// event and the resolutions it links to can be filtered/grouped the same
// way. Firms can extend this list from the form itself (see CATEGORY_KEY
// below); anything they add is merged in and persisted locally.
const BASE_CATEGORIES = [
  'Auditors & Accounts',
  'Borrowings & Charges',
  'Contracts & Agreements',
  'Directors & KMP',
  'Dividend',
  'Investments & Loans',
  'LLP Compliance',
  'Registered Office',
  'Share Capital',
  'Other',
];
const CATEGORY_KEY = 'cx_mca_event_categories';
function loadCustomCategories() {
  try { return JSON.parse(localStorage.getItem(CATEGORY_KEY) || '[]'); } catch { return []; }
}
function saveCustomCategory(name) {
  const current = loadCustomCategories();
  if (!current.includes(name)) {
    const next = [...current, name];
    localStorage.setItem(CATEGORY_KEY, JSON.stringify(next));
    return next;
  }
  return current;
}

// Every MCA e-form (361), grouped by functional area, for the ROC Forms
// Triggered picker — sourced from the full MCA forms master list.
const MCA_FORMS_BY_CATEGORY = {
  "Accounts & Audit": ["ADT-1","ADT-2","ADT-3","ADT-4","CRA-1","CRA-2","CRA-3","CRA-4","NFRA-1","NFRA-2"],
  "Accounts & CSR": ["CFI CSR","CSR-1"],
  "Address & Corporate Identity": ["INC-22","INC-22A","INC-23","INC-24","INC-25","INC-25A","INC-26","INC-28"],
  "Adjudication & Penalties": ["ADJ"],
  "Annual & Periodic Filings": ["AOC-1","AOC-2","AOC-3","AOC-3A","AOC-4","AOC-4CFS","AOC-4XBRL","AOC-5","MGT-7","MGT-7A","MGT-8","MSME Form I"],
  "Banking & Charges": ["CHG-1","CHG-2","CHG-3","CHG-4","CHG-5","CHG-6","CHG-7","CHG-8","CHG-9"],
  "Capital & Securities": ["PAS 7","PAS 8","PAS-1","PAS-2","PAS-3","PAS-4","PAS-5","PAS-6","SH-1","SH-10","SH-11","SH-12","SH-13","SH-14","SH-15","SH-2","SH-3","SH-4","SH-5","SH-6","SH-7","SH-8","SH-9"],
  "Company Incorporation & Setup": ["INC - 1","INC - 29","INC - 30","INC - 31","INC -2","INC-11","INC-11A","INC-11B","INC-11C","INC-12","INC-13","INC-14","INC-15","INC-16","INC-17","INC-18","INC-19","INC-20","INC-20A","INC-21","INC-27","INC-27A","INC-3","INC-32","INC-33","INC-34","INC-35","INC-5","INC-6","INC-7","INC-8","INC-9","RUN","SPICe+"],
  "Deposits & Borrowings": ["DPT-1","DPT-2","DPT-3","DPT-4"],
  "Directors & KMP Management": ["DIR-10","DIR-11","DIR-12","DIR-2","DIR-3","DIR-3A","DIR-3B","DIR-3C","DIR-3KYC","DIR-3KYCWeb","DIR-5","DIR-6","DIR-8","DIR-9","MR-1","MR-2","MR-3"],
  "Foreign Companies": ["FC-1","FC-2","FC-3","FC-4","FC-5"],
  "General Corporate Compliance": ["AOC-4CFSNBFC(IndAS)","AOC-4NBFC(IndAS)","Form No. MGT-9","URC-1","URC-2"],
  "General ROC Filings": ["GNL-1","GNL-2","GNL-3","GNL-4"],
  "IEPF Authority": ["IEPF-1","IEPF-1A","IEPF-2","IEPF-3","IEPF-4","IEPF-5","IEPF-5 e-verification report","IEPF-7"],
  "LLP Forms (LLP Act, 2008)": ["FiLLiP","RUN-LLP","LLP Form 3","LLP Form 4","LLP Form 5","LLP Form 8","LLP Form 9","LLP Form 11","LLP Form 12","LLP Form 15","LLP Form 22","LLP Form 23","LLP Form 24","LLP Form 25","LLP Form 27","LLP Form 28","LLP Form 31","LLP Form 32"],
  "Investigation & Enforcement (SFIO/MCA)": ["ARREST ORDER","CRL-1","Form - I","Form - II","Form - III","Form CRL-1","Form CSR-2","Form DIR-1","Form DIR-4","Form DIR-7","FORM MDC-2","FORM MDC-l","FORM-A","FORM-B","FORM-C","FORM-D","FORM-E","LEAP-1"],
  "Management & Governance": ["MGT-14","MGT-15"],
  "Mergers, Amalgamations & Compromises": ["CAA-1","CAA-10","CAA-10A","CAA-11","CAA-12","CAA-13","CAA-14","CAA-15","CAA-2","CAA-3","CAA-4","CAA-5","CAA-6","CAA-7","CAA-8","CAA-9","CAA. 16","FORM NO. CAA.1"],
  "NCLT Court Practice": ["NCLT 10","NCLT 11","NCLT 12","NCLT 13","NCLT 15","NCLT 16","NCLT 17","NCLT 18","NCLT 3","NCLT 3-B","NCLT 3-C","NCLT 4","NCLT 5","NCLT 7","NCLT 8","NCLT-1","NCLT-2","NCLT-6","NCLT-9"],
  "Nidhi Companies": ["NDH-1","NDH-2","NDH-3","NDH-4"],
  "Reduction of Share Capital": ["FORM NO. RSC - 3","FORM NO. RSC - 4","FORM NO. RSC - 5","FORM NO. RSC - 6","FORM NO. RSC -7","FORM NO. RSC-1","FORM NO. RSC-2"],
  "Regional Director Approvals": ["RD-1","RD-GNL-5"],
  "Secretarial Registers & Notices": ["MBP-1","MBP-2","MBP-3","MBP-4","MGT-1","MGT-11","MGT-12","MGT-13","MGT-2","MGT-3","MGT-4","MGT-5","MGT-6"],
  "Strike Off & Dormancy": ["MSC-1","MSC-2","MSC-3","MSC-4","MSC-5","STK-1","STK-2","STK-3","STK-3A","STK-4","STK-5","STK-5A","STK-6","STK-7","STK-8"],
  "Transparency & Ownership": ["BEN-1","BEN-2","BEN-3","BEN-4"],
  "Winding Up & Liquidation (WIN Series)": ["FORM WIN 25","FORM WIN 26","FORM WIN 27","FORM WIN 28","FORM WIN 29","FORM WIN 77","FORM WIN 78","FORM WIN 79","FORM WIN 80","FORM WIN 81","FORM WIN 82","WIN-1","WIN-10","WIN-11","WIN-12","WIN-13","WIN-14","WIN-15","WIN-16","WIN-17","WIN-18","WIN-19","WIN-2","WIN-20","WIN-21","WIN-22","WIN-23","WIN-24","WIN-3","WIN-30","WIN-31","WIN-32","WIN-33","WIN-34","WIN-35","WIN-36","WIN-37","WIN-38","WIN-38A","WIN-38B","WIN-38C","WIN-38D","WIN-38E","WIN-38F","WIN-38G","WIN-38H","WIN-38I","WIN-38J","WIN-38K","WIN-38L","WIN-38M","WIN-38N","WIN-38O","WIN-38P","WIN-38Q","WIN-38R","WIN-38S","WIN-38T","WIN-39","WIN-4","WIN-40","WIN-41","WIN-42","WIN-43","WIN-44","WIN-45","WIN-46","WIN-47","WIN-48","WIN-49","WIN-5","WIN-50","WIN-51","WIN-52","WIN-53","WIN-54","WIN-55","WIN-56","WIN-57","WIN-58","WIN-59","WIN-6","WIN-60","WIN-61","WIN-62","WIN-63","WIN-64","WIN-65","WIN-66","WIN-67","WIN-68","WIN-69","WIN-7","WIN-70","WIN-71","WIN-72","WIN-73","WIN-74","WIN-75","WIN-76","WIN-8","WIN-83","WIN-84","WIN-85","WIN-86","WIN-87","WIN-88","WIN-89","WIN-9","WIN-90","WIN-91","WIN-92","WIN-93","WIN-94","WIN-95"],
};
const ALL_MCA_FORMS = Object.values(MCA_FORMS_BY_CATEGORY).flat();

const MEETING_TYPES = ['BM', 'AGM', 'EGM'];

const ACCENTS = {
  navy:   { bg: 'bg-blue-50',   text: 'text-blue-700',   border: 'border-l-blue-400' },
  slate:  { bg: 'bg-gray-100',  text: 'text-gray-600',   border: 'border-l-gray-300' },
  green:  { bg: 'bg-emerald-50',text: 'text-emerald-700',border: 'border-l-emerald-400' },
  red:    { bg: 'bg-red-50',    text: 'text-red-700',    border: 'border-l-red-400' },
};

const Icon = {
  plus: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>,
  edit: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>,
  trash: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>,
  close: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
  checklist: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>,
};

function fetchJson(url, opts) {
  const token = localStorage.getItem('token');
  return fetch(`${BASE}${url}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...(opts?.headers || {}) },
  }).then(async r => {
    const data = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(data.message || 'Request failed');
    return data;
  });
}

function ConfirmModal({ message, title, confirmLabel = 'Confirm', onConfirm, onCancel }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,31,69,0.35)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div className="modal-surface" style={{ background: '#fff', borderRadius: 14, width: '100%', maxWidth: 400, padding: 24, boxShadow: '0 24px 60px rgba(0,0,0,0.2)' }}>
        {title && <p className="modal-title" style={{ fontWeight: 700, fontSize: 15, color: '#0f1f45', margin: '0 0 8px' }}>{title}</p>}
        <p className="modal-body" style={{ fontSize: 13.5, color: '#475569', margin: 0, lineHeight: 1.5 }}>{message}</p>
        <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
          <button onClick={onCancel} style={{ flex: 1, padding: '10px 0', borderRadius: 9, border: '1px solid #e2e8f0', background: '#fff', color: '#64748b', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
          <button onClick={onConfirm} style={{ flex: 1, padding: '10px 0', borderRadius: 9, border: 'none', background: '#b91c1c', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// EVENT TYPE FORM MODAL — create/edit an event type: what it's called,
// which ROC forms it triggers (in order), and which resolutions from
// Resolution Master apply (each pinned to one of that resolution's own
// allowed meeting types).
// ═══════════════════════════════════════════════════════════════════════
function EventFormModal({ event, resolutions, onSave, onClose }) {
  const isEdit = !!event?.id;
  const [categories, setCategories] = useState(() => [...new Set([...BASE_CATEGORIES, ...loadCustomCategories()])]);
  const [form, setForm] = useState({
    event_name: event?.event_name || '',
    event_tag: event?.event_tag || '',
    category: event?.category || categories[0],
    description: event?.description || '',
    is_active: event?.is_active !== false,
  });
  const [newCategoryInput, setNewCategoryInput] = useState('');
  const [formSequence, setFormSequence] = useState(
    (event?.form_sequence || []).length ? event.form_sequence : [{ form: ALL_MCA_FORMS[0], order: 1 }]
  );
  const [resolutionRefs, setResolutionRefs] = useState(event?.resolution_refs || []);
  const [syncActions, setSyncActions] = useState(event?.sync_actions || []);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const toggleSyncAction = (key) => setSyncActions(prev => prev.includes(key) ? prev.filter(x => x !== key) : [...prev, key]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const createCategory = () => {
    const name = newCategoryInput.trim();
    if (!name) return;
    if (!categories.includes(name)) {
      saveCustomCategory(name);
      setCategories(prev => [...prev, name]);
    }
    set('category', name);
    setNewCategoryInput('');
  };

  // Auto-derive the tag from the name until the user edits it directly —
  // same pattern Resolution Master expects (lowercase, underscored, unique).
  const [tagTouched, setTagTouched] = useState(isEdit);
  const handleNameChange = (v) => {
    set('event_name', v);
    if (!tagTouched) set('event_tag', v.toLowerCase().trim().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, ''));
  };

  const addFormRow = () => setFormSequence(prev => [...prev, { form: ALL_MCA_FORMS[0], order: prev.length + 1 }]);
  const updateFormRow = (idx, val) => setFormSequence(prev => prev.map((f, i) => i === idx ? { ...f, form: val } : f));
  const removeFormRow = (idx) => setFormSequence(prev => prev.filter((_, i) => i !== idx).map((f, i) => ({ ...f, order: i + 1 })));
  const moveFormRow = (idx, dir) => setFormSequence(prev => {
    const next = [...prev];
    const swap = idx + dir;
    if (swap < 0 || swap >= next.length) return prev;
    [next[idx], next[swap]] = [next[swap], next[idx]];
    return next.map((f, i) => ({ ...f, order: i + 1 }));
  });

  const addResolutionRow = () => {
    const first = resolutions[0];
    if (!first) return;
    setResolutionRefs(prev => [...prev, { resolution_id: first.id, meeting_type: (first.meeting_type_allowed || [])[0] || 'BM' }]);
  };
  const updateResolutionRow = (idx, patch) => setResolutionRefs(prev => prev.map((r, i) => i === idx ? { ...r, ...patch } : r));
  const removeResolutionRow = (idx) => setResolutionRefs(prev => prev.filter((_, i) => i !== idx));

  const handleSave = async () => {
    if (!form.event_name.trim()) { setError('Event name is required'); return; }
    if (!form.event_tag.trim()) { setError('Event tag is required'); return; }
    if (resolutionRefs.length === 0) { setError('Link at least one resolution'); return; }

    setSaving(true);
    setError('');
    try {
      const payload = {
        ...form,
        form_sequence: formSequence.filter(f => f.form),
        resolution_refs: resolutionRefs,
        sync_actions: syncActions,
      };
      const url = isEdit ? `/api/mca/events/${event.id}` : '/api/mca/events';
      const saved = await fetchJson(url, { method: isEdit ? 'PUT' : 'POST', body: JSON.stringify(payload) });
      onSave(saved);
    } catch (e) {
      setError(e.message || 'Could not save event type');
    } finally {
      setSaving(false);
    }
  };

  const inputCls = "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500";

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,31,69,0.35)', zIndex: 200, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '24px 16px', overflowY: 'auto' }}>
      <div className="bg-white modal-surface rounded-2xl w-full shadow-2xl" style={{ maxWidth: 720, marginBottom: 24 }}>
        <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between">
          <div>
            <p className="text-base font-bold text-[#0f1f45]">{isEdit ? 'Edit Event Type' : 'New Event Type'}</p>
            <p className="text-xs text-gray-400 mt-0.5">Define the forms it triggers and which resolutions apply.</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">✕</button>
        </div>

        <div className="px-6 py-5 space-y-5 max-h-[70vh] overflow-y-auto">
          {error && <div className="bg-red-50 border border-red-100 text-red-600 text-xs rounded-lg px-3 py-2">{error}</div>}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Event Name *</label>
              <input value={form.event_name} onChange={e => handleNameChange(e.target.value)} placeholder="e.g. Change in Registered Office" className={inputCls} />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Event Tag *</label>
              <input value={form.event_tag} onChange={e => { setTagTouched(true); set('event_tag', e.target.value); }} placeholder="e.g. registered_office_change" className={`${inputCls} font-mono`} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Category</label>
              <SearchableSelect
                value={form.category}
                options={[...categories].sort((a, b) => a === 'Other' ? 1 : b === 'Other' ? -1 : a.localeCompare(b)).map(c => ({ value: c, label: c }))}
                onChange={v => set('category', v)}
                placeholder="Select category"
                footer={({ close }) => (
                  <div className="p-2 flex items-center gap-1.5">
                    <input value={newCategoryInput} onChange={e => setNewCategoryInput(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') { createCategory(); close(); } }}
                      placeholder="New category name…" className="flex-1 border border-gray-200 rounded-md px-2 py-1.5 text-xs focus:outline-none focus:border-blue-500" />
                    <button type="button" onClick={() => { createCategory(); close(); }} disabled={!newCategoryInput.trim()}
                      className="px-2.5 py-1.5 rounded-md bg-[#0f1f45] text-white text-xs font-semibold disabled:opacity-40 flex items-center gap-1 flex-shrink-0">
                      {Icon.plus} Create
                    </button>
                  </div>
                )}
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Status</label>
              <select value={form.is_active ? 'active' : 'inactive'} onChange={e => set('is_active', e.target.value === 'active')} className={inputCls}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">Description</label>
            <textarea rows={2} value={form.description} onChange={e => set('description', e.target.value)} className={inputCls} />
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-xs font-semibold text-gray-600">ROC Forms Triggered <span className="font-normal text-gray-400">— in filing order</span></label>
              <button onClick={addFormRow} className="text-xs font-semibold text-blue-600 flex items-center gap-1">{Icon.plus} Add form</button>
            </div>
            <div className="space-y-2">
              {formSequence.map((f, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-blue-50 text-blue-700 text-[11px] font-bold flex items-center justify-center flex-shrink-0">{idx + 1}</span>
                  <div className="flex-1 min-w-0">
                    <SearchableSelect
                      value={f.form}
                      groups={Object.entries(MCA_FORMS_BY_CATEGORY).map(([label, forms]) => ({ label, options: forms.map(m => ({ value: m, label: m })) }))}
                      onChange={v => updateFormRow(idx, v)}
                      placeholder="Select form"
                    />
                  </div>
                  <button onClick={() => moveFormRow(idx, -1)} disabled={idx === 0} className="text-gray-400 disabled:opacity-30 px-1">↑</button>
                  <button onClick={() => moveFormRow(idx, 1)} disabled={idx === formSequence.length - 1} className="text-gray-400 disabled:opacity-30 px-1">↓</button>
                  <button onClick={() => removeFormRow(idx)} className="text-red-500 px-1">{Icon.trash}</button>
                </div>
              ))}
              {formSequence.length === 0 && <p className="text-xs text-gray-400">No forms added yet.</p>}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-xs font-semibold text-gray-600">Applicable Resolutions * <span className="font-normal text-gray-400">— from Resolution Master</span></label>
              <button onClick={addResolutionRow} disabled={resolutions.length === 0} className="text-xs font-semibold text-blue-600 flex items-center gap-1 disabled:opacity-40">{Icon.plus} Add resolution</button>
            </div>
            {resolutions.length === 0 ? (
              <p className="text-xs text-gray-400 border border-dashed border-gray-200 rounded-lg px-3 py-4 text-center">No resolutions found — create one in Resolution Master first.</p>
            ) : (
              <div className="space-y-2">
                {resolutionRefs.map((r, idx) => {
                  const res = resolutions.find(x => String(x.id) === String(r.resolution_id));
                  const allowedMeetings = res?.meeting_type_allowed?.length ? res.meeting_type_allowed : MEETING_TYPES;
                  return (
                    <div key={idx} className="flex items-center gap-2">
                      <div className="flex-1 min-w-0">
                        <SearchableSelect
                          value={r.resolution_id}
                          options={resolutions.map(res => ({ value: res.id, label: res.resolution_title, sub: res.resolution_code }))}
                          onChange={v => {
                            const next = resolutions.find(x => String(x.id) === String(v));
                            updateResolutionRow(idx, { resolution_id: Number(v), meeting_type: (next?.meeting_type_allowed || [])[0] || 'BM' });
                          }}
                          placeholder="Select resolution"
                        />
                      </div>
                      <select value={r.meeting_type} onChange={e => updateResolutionRow(idx, { meeting_type: e.target.value })} className="border border-gray-200 rounded-lg px-2 py-2 text-sm focus:outline-none focus:border-blue-500 flex-shrink-0" style={{ width: 76 }}>
                        {allowedMeetings.map(mt => <option key={mt} value={mt}>{mt}</option>)}
                      </select>
                      <button onClick={() => removeResolutionRow(idx)} className="text-red-500 px-1 flex-shrink-0">{Icon.trash}</button>
                    </div>
                  );
                })}
                {resolutionRefs.length === 0 && <p className="text-xs text-gray-400">No resolutions linked yet.</p>}
              </div>
            )}
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">
              On Approval, Sync To… <span className="font-normal text-gray-400">— what should update automatically once every form is approved</span>
            </label>
            <div className="space-y-1.5">
              {[
                { key: 'update_registered_office', label: 'Company Master — Registered Office', hint: 'reads event_data.new_address' },
                { key: 'update_company_name', label: 'Company Master — Company Name', hint: 'reads event_data.new_company_name' },
                { key: 'add_board_meeting_register', label: 'Statutory Registers — Board Meeting Register', hint: 'one entry per BM resolution in the case' },
                { key: 'add_general_meeting_register', label: 'Statutory Registers — General Meeting Register (AGM/EGM)', hint: 'one entry per AGM/EGM resolution in the case' },
                { key: 'update_director_master', label: 'Company Master — Add/Update Director', hint: 'reads each Passed BM resolution\'s field_values.additional_director_* (Appointment of Additional Director template)' },
                { key: 'update_director_cessation', label: 'Company Master — Mark Director Ceased', hint: 'reads each Passed BM resolution\'s field_values.resigning_director_* (Resignation of Director template)' },
                { key: 'update_auditor_master', label: 'Company Master — Statutory Auditor (First/Casual Vacancy/Removal)', hint: 'reads Passed RES-AUD-001/002/003/004/006 resolutions — First Auditor, casual-vacancy fill + EGM confirmation, and removal' },
              ].map(opt => (
                <label key={opt.key} className="flex items-start gap-2.5 px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer">
                  <input type="checkbox" className="mt-0.5" checked={syncActions.includes(opt.key)} onChange={() => toggleSyncAction(opt.key)} />
                  <span>
                    <span className="text-sm text-gray-800 block">{opt.label}</span>
                    <span className="text-[11px] text-gray-400">{opt.hint}</span>
                  </span>
                </label>
              ))}
            </div>
            {syncActions.length === 0 && (
              <p className="text-[11px] text-amber-600 mt-1.5">Nothing selected — cases created from this event will need Company Master / Registers updated manually after approval.</p>
            )}
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2.5 rounded-lg border border-gray-200 text-gray-600 text-sm font-semibold">Cancel</button>
          <button onClick={handleSave} disabled={saving}
            className="px-5 py-2.5 rounded-lg bg-[#0f1f45] text-white text-sm font-semibold disabled:opacity-50">
            {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Event Type'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// LIST VIEW
// ═══════════════════════════════════════════════════════════════════════
function EventsMaster() {
  const [events, setEvents] = useState([]);
  const [resolutions, setResolutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('All');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirm, setConfirm] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [e, r] = await Promise.all([fetchJson('/api/mca/events'), fetchJson('/api/mca/resolutions')]);
      setEvents(Array.isArray(e) ? e : []);
      setResolutions(Array.isArray(r) ? r : []);
    } catch { setEvents([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const handleDelete = (ev) => {
    setConfirm({
      title: 'Delete this event type?',
      message: `"${ev.event_name}" will be permanently deleted. Cases already filed under it are not affected.`,
      confirmLabel: 'Delete',
      onCancel: () => setConfirm(null),
      onConfirm: async () => {
        setConfirm(null);
        try { await fetchJson(`/api/mca/events/${ev.id}`, { method: 'DELETE' }); load(); } catch { /* row stays visible */ }
      },
    });
  };

  const filtered = events.filter(e => {
    const q = search.toLowerCase();
    const matchSearch = !q || e.event_name?.toLowerCase().includes(q) || e.event_tag?.toLowerCase().includes(q);
    const matchCategory = filterCategory === 'All' || e.category === filterCategory;
    return matchSearch && matchCategory;
  });

  const filterCategories = [...new Set([...BASE_CATEGORIES, ...loadCustomCategories(), ...events.map(e => e.category).filter(Boolean)])]
    .sort((a, b) => a === 'Other' ? 1 : b === 'Other' ? -1 : a.localeCompare(b));

  return (
    <div className="space-y-5">
      {confirm && <ConfirmModal {...confirm} />}
      {showModal && (
        <EventFormModal
          event={editing}
          resolutions={resolutions}
          onClose={() => { setShowModal(false); setEditing(null); }}
          onSave={() => { setShowModal(false); setEditing(null); load(); }}
        />
      )}

      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">Event types drive the "New Event Filing" wizard — each one defines the forms it triggers and which resolutions apply.</p>
        <button onClick={() => { setEditing(null); setShowModal(true); }}
          className="px-4 py-2.5 rounded-xl text-white text-sm font-semibold shadow-sm flex items-center gap-2 transition-shadow hover:shadow-md flex-shrink-0"
          style={{ background: 'linear-gradient(135deg, #0f1f45 0%, #1d3a72 100%)' }}>
          <span className="w-5 h-5 rounded-full bg-white/15 flex items-center justify-center text-xs">+</span>
          New Event Type
        </button>
      </div>

      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="p-4 border-b border-gray-100 flex flex-wrap items-center gap-3">
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search event name, tag…"
            className="flex-1 min-w-[200px] border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500" />
          <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)} className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none">
            <option value="All">All Categories</option>
            {filterCategories.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>

        {loading ? (
          <div className="py-16 text-center text-gray-400 text-sm">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center text-gray-400 text-sm">
            {events.length === 0 ? 'No event types yet — click "+ New Event Type" to define one.' : 'No event types match your filters.'}
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/60">
                {['Event', 'Category', 'Forms', 'Resolutions', 'Status', ''].map(h => (
                  <th key={h} className="px-4 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(e => (
                <tr key={e.id} className={`border-b border-gray-50 hover:bg-gray-50/60 border-l-4 ${e.is_active ? ACCENTS.navy.border : ACCENTS.slate.border}`}>
                  <td className="px-4 py-3">
                    <p className="text-sm font-semibold text-gray-800">{e.event_name}</p>
                    <p className="text-[11px] text-gray-400 mt-0.5 font-mono">{e.event_tag}</p>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">{e.category || '—'}</td>
                  <td className="px-4 py-3 text-xs text-gray-500 font-mono">{(e.form_sequence || []).map(f => f.form).join(', ') || '—'}</td>
                  <td className="px-4 py-3 text-xs text-gray-500">{(e.resolution_refs || []).length}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2.5 py-1 rounded-full text-[11px] font-semibold ${e.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>{e.is_active ? 'Active' : 'Inactive'}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1.5">
                      <button onClick={() => { setEditing(e); setShowModal(true); }} className="w-7 h-7 rounded-lg border border-gray-200 text-gray-500 flex items-center justify-center hover:bg-gray-50">{Icon.edit}</button>
                      <button onClick={() => handleDelete(e)} className="w-7 h-7 rounded-lg border border-gray-200 text-red-500 flex items-center justify-center hover:bg-red-50">{Icon.trash}</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default EventsMaster;